import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { type BlueprintResource, type ActionTemplateParameterResource, type ModifyBlueprintCommand, type IconMetadataResource, type IconSvgResource, canCommitTo, GetPrimaryPackageReference, type GitBranchResource, type IProcessResource, ProcessType, ProjectContextRepository, UseDefaultBranch, type WorkerPoolsSummaryResource, } from "@octopusdeploy/octopus-server-client";
import { links, optionalStringQueryParam, useQueryStringParam } from "@octopusdeploy/portal-routes";
import { isEqual, keyBy } from "lodash";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { BlueprintParametersTab, buildBlueprintParametersActions } from "~/areas/blueprints/BlueprintParametersTab";
import type { BlueprintSettings } from "~/areas/blueprints/EditBlueprintSettingsPage";
import { EditBlueprintSettingsPage } from "~/areas/blueprints/EditBlueprintSettingsPage";
import ProcessListLayoutForBlueprint from "~/areas/blueprints/processEditor/ProcessListLayoutForBlueprint";
import ProcessStepsLayoutForBlueprint, { intentFromFilter, ProcessPageIntent } from "~/areas/blueprints/processEditor/ProcessStepsLayoutForBlueprint";
import type { ProcessBlueprintContextProps } from "~/areas/projects/components/Process/Blueprints/BlueprintContext";
import { buildProcessBlueprintContextProps, ProcessBlueprintContext } from "~/areas/projects/components/Process/Blueprints/BlueprintContext";
import { isBlueprintProcessState, isRunOnServerOrWorkerPool, loadAvailableWorkerPools, MergedProcessResult, mergeProcesses, NoMergeRequiredResult, whereConfiguredToRun } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { loadProcess, useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { ProcessController } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { useProcessErrorActions } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { createDefaultFilter, ProcessQueryStringController, useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessWarningActions } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { hasSteps } from "~/areas/projects/components/Process/Contexts/processModelSelectors";
import type { ProcessStepsLayoutLoaderLookupData } from "~/areas/projects/components/Process/ProcessStepsLayoutLoader";
import { ExecutionLocation } from "~/areas/projects/components/Process/types";
import { ContextAddStepButton } from "~/areas/projects/components/Steps/index";
import type { GetCommitButtonProps } from "~/areas/projects/components/VersionControl/CommitButton";
import { GetCommitButton } from "~/areas/projects/components/VersionControl/CommitButton";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { getFormattedCommitMessage } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { ProjectContextProvider, useProjectContext } from "~/areas/projects/context/index";
import { client, repository } from "~/clientInstance";
import type { ActionPlugin } from "~/components/Actions/pluginRegistry";
import type { DoBusyTask, Errors } from "~/components/DataBaseComponent/index";
import { useDoBusyTaskEffect, DataBaseComponent } from "~/components/DataBaseComponent/index";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PaperLayoutVNext } from "~/components/PaperLayout/PaperLayoutVNext";
import * as tenantTagsets from "~/components/tenantTagsets";
import { createGlobalRequestContext } from "~/globalRequestContext";
import { TabItem, UrlNavigationTabsContainer } from "~/primitiveComponents/navigation/Tabs/index";
import type { GlobalState } from "~/store/reducers";
interface EditBlueprintsProps {
    blueprintId: string;
}
interface EditWithAllContextProps extends EditBlueprintsProps {
    doBusyTask: DoBusyTask;
    busy: Promise<void> | undefined;
    errors: Errors | undefined;
}
interface EditBlueprintsInternalProps extends EditWithAllContextProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    icons: IconSvgResource[];
    iconMetadata: IconMetadataResource | undefined;
    onChange: (blueprint: BlueprintResource) => void;
}
function EditWithAllContext({ blueprintId, doBusyTask, busy, errors }: EditWithAllContextProps) {
    const [lookupData, setLookupData] = useState<ProcessStepsLayoutLoaderLookupData>(defaultLookups);
    const [icons, setIcons] = useState<IconSvgResource[]>([]);
    const [iconMetadata, setIconMetadata] = useState<IconMetadataResource>();
    const [blueprintContextProps, setBlueprintContextProps] = useState<ProcessBlueprintContextProps>();
    const projectId = blueprintId.split("|")[0];
    const refreshLookups = useCallback(async () => {
        const environments = repository.Environments.all();
        const workerPools = repository.WorkerPools.all();
        const tagSets = tenantTagsets.getAll();
        setLookupData({
            ...defaultLookups,
            environmentsById: keyBy(await environments, "Id"),
            tagSets: await tagSets,
            workerPoolsById: keyBy(await workerPools, "Id"),
            machineRoles: await repository.MachineRoles.all(),
            tagIndex: await tenantTagsets.getTagIndex(),
            workerPoolsSummary: await repository.WorkerPools.summary(),
        });
    }, []);
    const onBlueprintChange = useCallback((blueprint: BlueprintResource) => {
        const context = buildProcessBlueprintContextProps(blueprint);
        setBlueprintContextProps(context);
    }, []);
    useDoBusyTaskEffect(doBusyTask, async () => {
        await refreshLookups();
        const icons = await repository.Icons.getIcons();
        setIcons(icons);
        const iconMetadata = await repository.Icons.getIconMetadata();
        setIconMetadata(iconMetadata);
    }, [refreshLookups]);
    return (<ProcessBlueprintContext.Provider value={blueprintContextProps}>
            <ProjectContextProvider doBusyTask={doBusyTask} projectIdOrSlug={projectId} gitRef={UseDefaultBranch}>
                {({ state }) => state.model ? (<ProcessController layoutActions={{ refreshLookupData: refreshLookups }} processType={ProcessType.Blueprint} id={blueprintId} doBusyTask={doBusyTask} project={state.model}>
                            {() => {
                return (<ProcessQueryStringController initialQueryFilter={createDefaultFilter()}>
                                        {() => {
                        return (<EditBlueprintPageInternal blueprintId={blueprintId} doBusyTask={doBusyTask} busy={busy} errors={errors} lookupData={lookupData} icons={icons} iconMetadata={iconMetadata} onChange={onBlueprintChange}/>);
                    }}
                                    </ProcessQueryStringController>);
            }}
                        </ProcessController>) : (<PaperLayoutVNext busy={true} fullWidth={true}></PaperLayoutVNext>)}
            </ProjectContextProvider>
        </ProcessBlueprintContext.Provider>);
}
const defaultLookups = {
    includedScriptModules: [],
    lifecyclePreview: null,
    environmentsById: {},
    channelsById: null,
    tagSets: [],
    workerPoolsById: {},
    machineRoles: [],
    tagIndex: {},
    userOnboarding: null,
    workerPoolsSummary: {
        WorkerPoolSummaries: [],
        TotalMachines: 0,
        TotalDisabledMachines: 0,
        MachineHealthStatusSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        MachineEndpointSummaries: {
            None: 0,
            TentaclePassive: 0,
            TentacleActive: 0,
            Ssh: 0,
            OfflineDrop: 0,
            AzureWebApp: 0,
            AzureCloudService: 0,
            AzureServiceFabricCluster: 0,
            Kubernetes: 0,
            KubernetesTentacle: 0,
            StepPackage: 0,
        },
        DeploymentTargetSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        TentacleUpgradesRequired: false,
        MachineIdsForCalamariUpgrade: [],
        MachineIdsForTentacleUpgrade: [],
    },
    projectTriggers: [],
};
interface ProcessStepsOrListLayoutProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    busy: Promise<void> | undefined;
    doBusyTask: DoBusyTask;
    errors: Errors | undefined;
}
function ProcessStepsOrListLayout({ lookupData, busy, doBusyTask, errors }: ProcessStepsOrListLayoutProps) {
    const processQueryStringContext = useProcessQueryStringContext();
    const isBuiltInWorkerEnabled = useSelector((state: GlobalState) => state.configurationArea.features.isBuiltInWorkerEnabled);
    const intent = intentFromFilter(processQueryStringContext.state.queryFilter);
    if (intent === ProcessPageIntent.Unknown) {
        return <ProcessListLayoutForBlueprint processType={ProcessType.Blueprint} lookups={lookupData} errors={errors} busy={!!busy} doBusyTask={doBusyTask} isBuiltInWorkerEnabled={isBuiltInWorkerEnabled}/>;
    }
    return <ProcessStepsLayoutForBlueprint lookups={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask} isBuiltInWorkerEnabled={isBuiltInWorkerEnabled}/>;
}
function applyCommonLogicToProcessResource(process: IProcessResource, workerPoolsSummary: WorkerPoolsSummaryResource, getPluginForAction: (actionId: string) => ActionPlugin) {
    const availableWorkerPools = loadAvailableWorkerPools(workerPoolsSummary);
    process.Steps.forEach((step) => {
        step.Actions.forEach((action) => {
            const plugin = getPluginForAction(action.Id);
            const runOn = whereConfiguredToRun(!!step.Properties["Octopus.Action.TargetRoles"], action, availableWorkerPools, plugin);
            if (runOn) {
                if (!isRunOnServerOrWorkerPool(runOn)) {
                    action.Container = { FeedId: null, Image: null, GitUrl: null, Dockerfile: null };
                }
                else {
                    if (runOn.executionLocation === ExecutionLocation.OctopusServer || runOn.executionLocation === ExecutionLocation.WorkerPool) {
                        step.Properties["Octopus.Action.TargetRoles"] = "";
                    }
                    if (runOn.executionLocation !== ExecutionLocation.WorkerPool && runOn.executionLocation !== ExecutionLocation.WorkerPoolForRoles) {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        action.WorkerPoolId = null!;
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        action.WorkerPoolVariable = null!;
                    }
                    action.Container = runOn.container;
                }
            }
            if (!action.Name || action.Name.length === 0) {
                const primaryPackage = GetPrimaryPackageReference(action.Packages);
                if (primaryPackage) {
                    action.Name = primaryPackage.PackageId;
                }
            }
        });
    });
}
function EditBlueprintPageInternal({ doBusyTask, busy, errors, lookupData, icons, iconMetadata, onChange }: EditBlueprintsInternalProps) {
    const project = useProjectContext();
    const process = useProcessContext();
    const errorActions = useProcessErrorActions();
    const warningActions = useProcessWarningActions();
    const navigation = useSpaceAwareNavigation();
    const blueprint = isBlueprintProcessState(process.state.model.process) ? process.state.model.process : undefined;
    const [commitMessage, setCommitMessage] = useState<CommitMessageWithDetails>({ summary: "", details: "" });
    const [disableDirtyFormChecking, setDisableDirtyFormChecking] = useState<boolean>(false);
    const [blueprintSettings, setBlueprintSettings] = useState<BlueprintSettings | undefined>();
    const [cleanBlueprintSettings, setCleanBlueprintSettings] = useState<BlueprintSettings | undefined>();
    const [parameters, setParameters] = useState<ActionTemplateParameterResource[]>();
    const [cleanParameters, setCleanParameters] = useState<ActionTemplateParameterResource[]>();
    const [secondaryActions, setSecondaryActions] = useState<PageAction[]>([addStepButton]);
    const activeTabQueryParameter = optionalStringQueryParam("activeTab");
    const [activeTab, _] = useQueryStringParam(activeTabQueryParameter);
    const onParametersChanged = useCallback((parameters: ActionTemplateParameterResource[]) => {
        setParameters(parameters);
        if (blueprint) {
            const resource: BlueprintResource = { ...blueprint, Parameters: parameters };
            onChange(resource);
        }
    }, [onChange, blueprint]);
    const onSettingsChange = useCallback((settings: BlueprintSettings) => {
        setBlueprintSettings(settings);
        if (blueprint) {
            const resource: BlueprintResource = { ...blueprint, ...settings };
            onChange(resource);
        }
    }, [onChange, blueprint]);
    const refresh = useCallback((resource: BlueprintResource) => {
        setCleanBlueprintSettings(resource);
        setBlueprintSettings(resource);
        setCleanParameters(resource.Parameters);
        setParameters(resource.Parameters);
        onChange(resource);
    }, [onChange]);
    useEffect(() => {
        if (activeTab === "process") {
            setSecondaryActions([addStepButton]);
        }
        else if (activeTab === "settings") {
            setSecondaryActions([]);
        }
        else if (activeTab === "parameters") {
            const parametersActions = buildBlueprintParametersActions({ parameters: parameters || [], onChange: onParametersChanged });
            setSecondaryActions(parametersActions);
        }
    }, [activeTab, parameters, onParametersChanged]);
    useEffect(() => {
        if (blueprint && (blueprintSettings === undefined || parameters === undefined)) {
            refresh(blueprint);
        }
    }, [blueprint, blueprintSettings, parameters, refresh]);
    if (!project.state.model) {
        return null;
    }
    let openCommitDialog: (() => void) | undefined = undefined;
    function getDefaultCommitMessage(): string {
        return "Update blueprint";
    }
    const saveProcess = async (isNavigationConfirmation: boolean, newBranch?: GitBranchResource) => {
        let isValid = true;
        const onSuccess: () => void = () => {
            errorActions.clearErrors();
            warningActions.clearWarnings();
        };
        const onError: (errors: Errors) => void = (errors) => {
            errorActions.setErrors(errors, process.selectors);
            isValid = false;
            warningActions.clearWarnings();
        };
        if (isNavigationConfirmation && openCommitDialog) {
            await openCommitDialog();
            return isValid;
        }
        else {
            await doBusyTask(async () => {
                //TODO: this merge conflict logic can be moved into a side effect in the reducer if we
                if (!process.state.model.process?.Id) {
                    throw Error("Failed to find processId");
                }
                const { cleanModelProcess: clientCleanProcessResource, modelProcess: clientProcessResource } = process.selectors.getProcessesForMerge();
                applyCommonLogicToProcessResource(clientProcessResource, lookupData.workerPoolsSummary, process.selectors.getActionPlugin);
                applyCommonLogicToProcessResource(clientCleanProcessResource, lookupData.workerPoolsSummary, process.selectors.getCleanActionPlugin);
                const branchRepository = newBranch ? new ProjectContextRepository(client, project.state.model, newBranch, createGlobalRequestContext("ProjectContext")) : project.state.projectContextRepository;
                const serverProcessResource = await loadProcess(branchRepository, process.selectors.getProcessType(), process.state.model.process.Id, project.state.model, project.state.gitRef);
                const mergeResult = mergeProcesses(clientCleanProcessResource, clientProcessResource, serverProcessResource);
                if (mergeResult instanceof NoMergeRequiredResult) {
                    if (blueprintSettings && blueprint) {
                        const updatedBlueprint: ModifyBlueprintCommand = {
                            ...blueprint,
                            Name: blueprintSettings.Name,
                            Description: blueprintSettings.Description,
                            Icon: blueprintSettings.Icon,
                            Steps: mergeResult.value.Steps,
                            ChangeDescription: getFormattedCommitMessage(commitMessage, getDefaultCommitMessage()),
                            Parameters: parameters || [],
                        };
                        const result = await repository.Blueprints.modify(updatedBlueprint);
                        if (result !== null) {
                            setCommitMessage({ summary: "", details: "" });
                            refresh(result);
                            await project.actions.refreshModel();
                        }
                    }
                }
                else if (mergeResult instanceof MergedProcessResult) {
                    process.actions.conflictDetected(serverProcessResource, mergeResult.value);
                }
            }, { onError, onSuccess });
            if (isValid && !hasSteps(process.state.model)()) {
                navigation.navigate(links.editBlueprintPage.generateUrl({ blueprintId: process.state.model.process?.Id ?? "", spaceId: process.state.model.process?.SpaceId ?? "" }));
            }
            return isValid;
        }
    };
    const onNewBranchCreating = async (branchName: string) => {
        let newBranchResource: GitBranchResource | null = null;
        try {
            newBranchResource = await project.state.projectContextRepository.Branches.createBranch(project.state.model, branchName, project.state.gitRef?.CanonicalName ?? "");
        }
        catch (ex) {
            setDisableDirtyFormChecking(false);
            throw ex;
        }
        if (newBranchResource) {
            setDisableDirtyFormChecking(true);
            if (await saveProcess(false, newBranchResource)) {
                project.actions.changeGitRef(branchName);
            }
            setDisableDirtyFormChecking(false);
        }
    };
    function getCommitButtonProps(): GetCommitButtonProps {
        return {
            project: project.state.model,
            gitRef: project.state.gitRef?.CanonicalName,
            canCommitToGitRef: canCommitTo(project.state.gitRef),
            defaultCommitMessage: getDefaultCommitMessage(),
            commitMessage: commitMessage,
            updateCommitMessage: (commitMessage: CommitMessageWithDetails) => setCommitMessage(commitMessage),
            commitMessageAccessibleName: `Commit message for saving the blueprint process`,
            commitDetailsAccessibleName: `Commit details for saving the blueprint process`,
            commitButtonAccessibleName: `Commit changes to the blueprint process`,
            onInitializing: (openDialog) => (openCommitDialog = openDialog),
            label: "Commit",
            busyLabel: "Committing",
            onClick: (e) => saveProcess(false),
            disabled: disableDirtyFormChecking ? false : isEqual({ ...process.state.model, ...blueprintSettings, Parameters: parameters }, { ...process.state.cleanModel, ...cleanBlueprintSettings, Parameters: cleanParameters }),
        };
    }
    const connectRepositoryAction: PrimaryPageAction = {
        type: "custom",
        content: <GetCommitButton {...getCommitButtonProps()} onNewBranchCreating={onNewBranchCreating}/>,
        key: "Get Commit",
    };
    if (blueprintSettings === undefined) {
        return <PaperLayoutVNext busy={true} fullWidth={true}/>;
    }
    return (<PaperLayoutVNext title={blueprintSettings.Name ?? "Edit Blueprint"} errors={errors} busy={busy} fullWidth={true} primaryAction={connectRepositoryAction} pageActions={secondaryActions}>
            <UrlNavigationTabsContainer defaultValue={"process"}>
                <TabItem label="Process" value="process">
                    <ProcessStepsOrListLayout lookupData={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask}/>
                </TabItem>
                <TabItem label="Parameters" value="parameters">
                    <BlueprintParametersTab parameters={parameters || []} onChange={onParametersChanged}/>
                </TabItem>
                <TabItem label="Settings" value="settings">
                    <EditBlueprintSettingsPage blueprintSettings={blueprintSettings} onChange={onSettingsChange} iconSvgResources={icons} iconMetadata={iconMetadata}/>
                </TabItem>
            </UrlNavigationTabsContainer>
        </PaperLayoutVNext>);
}
const addStepButton: PageAction = {
    type: "custom",
    content: <ContextAddStepButton />,
    key: "Add Step",
};
export class EditBlueprintPage extends DataBaseComponent<EditBlueprintsProps> {
    constructor(props: EditBlueprintsProps) {
        super(props);
        this.state = {};
    }
    render() {
        return <EditWithAllContext blueprintId={this.props.blueprintId} doBusyTask={this.doBusyTask} busy={this.state.busy} errors={this.errors}/>;
    }
    static displayName = "EditBlueprintPage";
}
